/**
 * Report text with `{{token}}` figures (E14-design §4, §5). The AI and the
 * template write a token where a number goes; the report's token table
 * answers it. These helpers are pure so the editor's rules are tested
 * without a browser:
 *
 *   - `splitTokens` cuts text into words and tokens for the reading form;
 *   - `guardTokenEdit` keeps a token whole through an edit: typing inside
 *     one lands after it, and deleting part of one deletes all of it;
 *   - `snapSelection` keeps the caret and the selection off a token's
 *     inside, so it moves and deletes as a unit.
 *
 * Mirrors `packages/pdf-render/src/placeholders.ts` (the token pattern) and
 * `vendor-report.ts` (bullet items), so the editor, the read and the PDF
 * read text the same way.
 */

const TOKEN = /\{\{([^{}]*)\}\}/g;

export type TokenValues = Readonly<Record<string, string>>;

export type TextPart =
  | { kind: 'text'; text: string }
  /** `value` null: nothing in the token table answers the token. */
  | { kind: 'token'; name: string; raw: string; value: string | null };

function has(values: TokenValues, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(values, key);
}

export function splitTokens(text: string, values: TokenValues): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) });
    const name = (m[1] ?? '').trim();
    parts.push({ kind: 'token', name, raw: m[0], value: has(values, name) ? values[name]! : null });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts;
}

/** Where each token sits: `[start, end)` in the text, in order. */
export function tokenRanges(text: string): Array<[number, number]> {
  return [...text.matchAll(TOKEN)].map((m) => [m.index ?? 0, (m.index ?? 0) + m[0].length]);
}

/** The tokens the table does not answer, each once, as written (`{{vendor.9.name}}`). */
export function unknownTokens(text: string, values: TokenValues): string[] {
  const out: string[] = [];
  for (const p of splitTokens(text, values)) {
    if (p.kind === 'token' && p.value === null && !out.includes(p.raw)) out.push(p.raw);
  }
  return out;
}

/** The text with every token filled (an em dash where none answers), as the PDF prints it. */
export function fillTokens(text: string, values: TokenValues): string {
  return splitTokens(text, values)
    .map((p) => (p.kind === 'text' ? p.text : (p.value ?? '—')))
    .join('');
}

/** A bullet block's items: one per line, a leading "-", "*" or "•" dropped. */
export function bulletItems(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.replace(/^\s*[-*•]\s+/, '').trim())
    .filter((line) => line.length > 0);
}

export interface GuardedEdit {
  text: string;
  /** Where the caret goes after the edit. */
  caret: number;
}

/**
 * An edit from `prev` to `next` that keeps every token of `prev` whole.
 * The edit is the one span that changed (common prefix and suffix
 * removed). Text inserted strictly inside a token is placed after it; a
 * deletion or replacement that takes part of a token takes all of it.
 * Tokens the edit leaves alone, or covers entirely, are untouched.
 */
export function guardTokenEdit(prev: string, next: string): GuardedEdit {
  if (prev === next) return { text: next, caret: next.length };
  const max = Math.min(prev.length, next.length);
  let p = 0;
  while (p < max && prev[p] === next[p]) p += 1;
  let s = 0;
  while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s += 1;
  let start = p;
  let end = prev.length - s;
  const inserted = next.slice(p, next.length - s);
  const ranges = tokenRanges(prev);

  if (start === end) {
    // A pure insertion: inside a token, it goes after the token.
    const inside = ranges.find(([a, b]) => start > a && start < b);
    if (!inside) return { text: next, caret: start + inserted.length };
    const at = inside[1];
    return {
      text: prev.slice(0, at) + inserted + prev.slice(at),
      caret: at + inserted.length,
    };
  }

  // A deletion or replacement: widen it over any token it only partly takes.
  for (const [a, b] of ranges) {
    const overlaps = start < b && end > a;
    const covers = start <= a && end >= b;
    if (overlaps && !covers) {
      start = Math.min(start, a);
      end = Math.max(end, b);
    }
  }
  return {
    text: prev.slice(0, start) + inserted + prev.slice(end),
    caret: start + inserted.length,
  };
}

/**
 * A selection kept off the inside of tokens: a caret inside one moves to
 * its nearer edge (the end on a tie); a selection that ends inside one
 * widens over it.
 */
export function snapSelection(
  text: string,
  start: number,
  end: number,
): { start: number; end: number } {
  const ranges = tokenRanges(text);
  if (start === end) {
    const inside = ranges.find(([a, b]) => start > a && start < b);
    if (!inside) return { start, end };
    const [a, b] = inside;
    const edge = start - a < b - start ? a : b;
    return { start: edge, end: edge };
  }
  let s = start;
  let e = end;
  for (const [a, b] of ranges) {
    if (s > a && s < b) s = a;
    if (e > a && e < b) e = b;
  }
  return { start: s, end: e };
}
